/**
 * The school sign-up form's two decisions, pure.
 *
 * 🔴 IT LIVES HERE AND NOT IN THE `.tsx` FOR THE REASON EVERY OTHER MODEL FILE
 * IN THIS REPO DOES: there is no DOM renderer (OD-60), so a rule written inside
 * a component is a rule no test can reach — and these two decide whether the
 * only self-service door into this product opens at all.
 *
 * 🔴 THE BUG THAT PUT THEM HERE. `publicPlans()` filters on `isActive AND
 * isPublic`, and the operator's create-plan form defaults `isPublic` to off, so
 * a deployment with no published plan is the ordinary early state rather than a
 * fault. The form answered it by rendering a "Price on request" option worth the
 * empty string — which `canRequestSignup` refuses, and which
 * `schoolSignupSchema` (`planCode: min(1)`) would refuse after it. The result
 * was a completed form under a permanently disabled button, with nothing on
 * screen naming the cause.
 */

/**
 * What the price list turned out to be.
 *
 * ⚠️ `unavailable` DELIBERATELY MERGES "PUBLISHED NOTHING" WITH "COULD NOT ASK".
 * The school's position is identical in both — there is no code they may send —
 * and a screen that distinguished them would be reporting our plumbing to
 * somebody who can act on neither.
 */
export type PlanListState = 'loading' | 'ready' | 'unavailable'

/**
 * ⚠️ AN EMPTY LIST IS NOT A LOADED LIST. A 200 carrying `[]` is the exact
 * response production returns today, and treating it as `ready` is what left a
 * select holding one unusable option.
 */
export function planListState(rows: readonly { code: string }[]): PlanListState {
  return rows.length === 0 ? 'unavailable' : 'ready'
}

/**
 * The number of places, or `null` if what was typed is not one.
 *
 * ⚠️ `Number.parseInt` STOPS AT THE FIRST NON-DIGIT, so `'12abc'` parses as 12.
 * The field strips non-digits as they are typed, which makes that unreachable
 * from the keyboard — but this function is the rule, and a rule that is only
 * correct because of its caller is one that breaks when a second caller appears.
 */
export function seatCount(seats: string): number | null {
  if (!/^\d+$/.test(seats.trim())) return null
  const n = Number.parseInt(seats, 10)
  return Number.isSafeInteger(n) && n > 0 ? n : null
}

export interface SignupDraft {
  schoolName: string
  adminName: string
  adminEmail: string
  /** `Plan.code` off the published list. The empty string means none was offered. */
  planCode: string
  /** Exactly what is in the box, unparsed. */
  seats: string
}

/**
 * May this be sent?
 *
 * 🔴 IT REFUSES AN EMPTY `planCode`, AND THAT REFUSAL IS CORRECT — the server
 * refuses it too, so enabling the button here would only move the dead end one
 * round trip later and dress it as a server error. What was wrong was never this
 * gate; it was that nothing told the school why it was shut. `planListState`
 * exists so the screen can say so.
 */
export function canRequestSignup(draft: SignupDraft): boolean {
  return (
    draft.schoolName.trim().length > 1 &&
    draft.adminName.trim().length > 0 &&
    draft.adminEmail.trim().length > 3 &&
    draft.planCode !== '' &&
    seatCount(draft.seats) !== null
  )
}
